/**
 * Loads the Google Maps JavaScript API on demand and memoizes the
 * result so every location-autocomplete instance on the page shares
 * one download.
 *
 * The `places` library is requested via `loading=async`, which exposes
 * `google.maps.importLibrary()` for callers to pull in the Places (New)
 * classes (`AutocompleteSuggestion`, `AutocompleteSessionToken`, `Place`).
 */

let mapsPromise = null;

const READY_CALLBACK = '__gigsaladGoogleMapsReady';

const MAPS_JS_API_URL = 'https://maps.googleapis.com/maps/api/js';

// The script can download without ever invoking the ready callback (e.g. a
// rejected referrer/key that Google only logs to the console), which would
// leave the memoized promise pending forever. Reject after this so callers can
// surface an error and a later attempt can retry.
const LOAD_TIMEOUT_MS = 10000;

/**
 * @param {{ apiKey: string }} options
 * @returns {Promise<typeof google.maps>}
 */
export function loadGoogleMaps({ apiKey }) {
    if (mapsPromise) {
        return mapsPromise;
    }

    const promise = new Promise((resolve, reject) => {
        // Reuse the API if another script already loaded it.
        if (window.google?.maps?.importLibrary) {
            resolve(window.google.maps);
            return;
        }

        if (!apiKey) {
            reject(new Error('Google Maps API key is missing! Must set API key.'));
            return;
        }

        // A timed-out attempt is rejected but its script stays in the DOM and
        // can still fire a late ready/error event after the next keystroke has
        // started a fresh attempt. Each attempt therefore settles exactly once
        // and tears down only what it owns: it clears the memoized promise and
        // the global callback only while they still point at this attempt, so a
        // stale event from a superseded attempt is a no-op rather than nulling
        // the in-flight promise or deleting the live callback.
        let settled = false;

        const onReady = () => {
            if (settled) {
                return;
            }
            teardown();
            resolve(window.google.maps);
        };

        const fail = (error) => {
            if (settled) {
                return;
            }
            teardown();
            if (mapsPromise === promise) {
                mapsPromise = null; // Allow a later retry.
            }
            reject(error);
        };

        const timeoutId = setTimeout(() => fail(new Error('Timed out loading the Google Maps JavaScript API.')), LOAD_TIMEOUT_MS);

        const params = new URLSearchParams({
            key: apiKey,
            libraries: 'places',
            loading: 'async',
            callback: READY_CALLBACK,
        });

        const script = document.createElement('script');
        script.src = `${MAPS_JS_API_URL}?${params.toString()}`;
        script.async = true;
        script.onerror = () => fail(new Error('Failed to load the Google Maps JavaScript API.'));

        const teardown = () => {
            settled = true;
            clearTimeout(timeoutId);
            script.onerror = null;
            if (window[READY_CALLBACK] === onReady) {
                delete window[READY_CALLBACK];
            }
        };

        window[READY_CALLBACK] = onReady;
        document.head.appendChild(script);
    });

    mapsPromise = promise;

    return mapsPromise;
}
